import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

// ═══════════════════════════════════════════════════════════════════════════════
// PINDAY SKELETON SYSTEM - Elegant Loading States
// ═══════════════════════════════════════════════════════════════════════════════

// Common skeleton wrapper with consistent styling
const SkeletonCard = ({ className, children }: { className?: string; children: React.ReactNode }) => (
  <div className={cn(
    'bg-card rounded-[var(--pd-radius)] border border-border/50 p-6 space-y-4',
    className
  )}>
    {children}
  </div>
)

// ─────────────────────────────────────────────────────────────────────────────
// DASHBOARD SKELETON
// ─────────────────────────────────────────────────────────────────────────────
export function DashboardSkeleton() {
  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header Section */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-32" />
        </div>
        <Skeleton className="h-10 w-10 rounded-full" />
      </div>

      {/* Plan Banner */}
      <Skeleton className="h-16 w-full rounded-[var(--pd-radius)]" />

      {/* Metrics Cards - 4 cards grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map(i => (
          <SkeletonCard key={i} className="p-4">
            <div className="flex items-center gap-3">
              <Skeleton className="h-10 w-10 rounded-xl" />
              <div className="space-y-2 flex-1">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-6 w-16" />
              </div>
            </div>
          </SkeletonCard>
        ))}
      </div>

      {/* Main content grid */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Needs Attention Card */}
        <SkeletonCard>
          <div className="flex items-center gap-2">
            <Skeleton className="h-5 w-5 rounded" />
            <Skeleton className="h-5 w-32" />
          </div>
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-muted/30">
                <Skeleton className="h-8 w-8 rounded-lg" />
                <div className="space-y-1 flex-1">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </SkeletonCard>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Upcoming Bookings */}
          <SkeletonCard>
            <div className="flex items-center justify-between">
              <Skeleton className="h-5 w-36" />
              <Skeleton className="h-8 w-20 rounded-lg" />
            </div>
            <div className="space-y-2">
              {[1, 2].map(i => (
                <div key={i} className="flex items-center gap-3 p-2 rounded-lg">
                  <Skeleton className="h-12 w-12 rounded-xl" />
                  <div className="space-y-1 flex-1">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                  <Skeleton className="h-6 w-16 rounded-full" />
                </div>
              ))}
            </div>
          </SkeletonCard>

          {/* Activity Feed */}
          <SkeletonCard>
            <Skeleton className="h-5 w-28" />
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="flex items-start gap-3">
                  <Skeleton className="h-2 w-2 rounded-full mt-2" />
                  <div className="space-y-1 flex-1">
                    <Skeleton className="h-3 w-full" />
                    <Skeleton className="h-3 w-16" />
                  </div>
                </div>
              ))}
            </div>
          </SkeletonCard>
        </div>
      </div>

      {/* Quick Actions */}
      <SkeletonCard className="p-4">
        <div className="flex gap-3 overflow-hidden">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-12 w-32 rounded-xl flex-shrink-0" />
          ))}
        </div>
      </SkeletonCard>

      {/* Usage Bar */}
      <div className="grid lg:grid-cols-2 gap-6">
        <SkeletonCard className="p-4">
          <div className="flex items-center justify-between mb-2">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-16" />
          </div>
          <Skeleton className="h-3 w-full rounded-full" />
        </SkeletonCard>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// PROPOSALS PAGE SKELETON
// ─────────────────────────────────────────────────────────────────────────────
export function ProposalsSkeleton() {
  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Skeleton className="h-8 w-40" />
          <Skeleton className="h-4 w-56" />
        </div>
        <Skeleton className="h-10 w-36 rounded-xl" />
      </div>

      {/* Filters */}
      <div className="flex gap-3">
        <Skeleton className="h-10 w-64 rounded-xl" />
        <Skeleton className="h-10 w-32 rounded-xl" />
      </div>

      {/* Proposals Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3, 4, 5, 6].map(i => (
          <SkeletonCard key={i}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="space-y-1">
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="h-3 w-36" />
                </div>
              </div>
              <Skeleton className="h-6 w-20 rounded-full" />
            </div>
            <div className="pt-3 border-t border-border/50 space-y-2">
              <div className="flex items-center gap-2">
                <Skeleton className="h-4 w-4 rounded" />
                <Skeleton className="h-3 w-24" />
              </div>
              <div className="flex items-center gap-2">
                <Skeleton className="h-4 w-4 rounded" />
                <Skeleton className="h-3 w-32" />
              </div>
            </div>
            <div className="flex items-center justify-between pt-2">
              <Skeleton className="h-5 w-20" />
              <Skeleton className="h-8 w-24 rounded-lg" />
            </div>
          </SkeletonCard>
        ))}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// PACKAGES PAGE SKELETON
// ─────────────────────────────────────────────────────────────────────────────
export function PackagesSkeleton() {
  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Skeleton className="h-8 w-44" />
          <Skeleton className="h-4 w-64" />
        </div>
        <Skeleton className="h-10 w-36 rounded-xl" />
      </div>

      {/* Limit Warning Banner */}
      <Skeleton className="h-12 w-full rounded-xl" />

      {/* Packages Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1, 2, 3].map(i => (
          <SkeletonCard key={i} className="overflow-hidden p-0">
            {/* Image placeholder */}
            <Skeleton className="h-40 w-full rounded-none" />
            <div className="p-6 space-y-4">
              <div className="space-y-2">
                <Skeleton className="h-6 w-3/4" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-2/3" />
              </div>
              {/* Benefits */}
              <div className="space-y-2">
                {[1, 2, 3].map(j => (
                  <div key={j} className="flex items-center gap-2">
                    <Skeleton className="h-4 w-4 rounded-full" />
                    <Skeleton className="h-3 w-32" />
                  </div>
                ))}
              </div>
              {/* Price & Actions */}
              <div className="flex items-center justify-between pt-4 border-t border-border/50">
                <Skeleton className="h-7 w-24" />
                <div className="flex gap-2">
                  <Skeleton className="h-9 w-9 rounded-lg" />
                  <Skeleton className="h-9 w-9 rounded-lg" />
                </div>
              </div>
            </div>
          </SkeletonCard>
        ))}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// CONTRACT TEMPLATES SKELETON
// ─────────────────────────────────────────────────────────────────────────────
export function ContractTemplatesSkeleton() {
  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Skeleton className="h-8 w-52" />
          <Skeleton className="h-4 w-72" />
        </div>
        <Skeleton className="h-10 w-40 rounded-xl" />
      </div>

      {/* Templates List */}
      <div className="space-y-4">
        {[1, 2, 3].map(i => (
          <SkeletonCard key={i} className="flex items-center justify-between p-4">
            <div className="flex items-center gap-4">
              <Skeleton className="h-12 w-12 rounded-xl" />
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Skeleton className="h-5 w-40" />
                  {i === 1 && <Skeleton className="h-5 w-16 rounded-full" />}
                </div>
                <div className="flex items-center gap-4">
                  <Skeleton className="h-3 w-20" />
                  <Skeleton className="h-3 w-28" />
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Skeleton className="h-9 w-9 rounded-lg" />
              <Skeleton className="h-9 w-9 rounded-lg" />
              <Skeleton className="h-9 w-9 rounded-lg" />
            </div>
          </SkeletonCard>
        ))}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// INTEGRATIONS PAGE SKELETON
// ─────────────────────────────────────────────────────────────────────────────
export function IntegrationsSkeleton() {
  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header */}
      <div className="space-y-2">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-4 w-80" />
      </div>

      {/* Category Sections */}
      {['Calendário', 'Pagamentos'].map((_, catIndex) => (
        <div key={catIndex} className="space-y-4">
          <Skeleton className="h-5 w-24" />
          <div className="grid md:grid-cols-2 gap-4">
            {[1, 2].map(i => (
              <SkeletonCard key={i} className="flex items-start gap-4">
                <Skeleton className="h-14 w-14 rounded-2xl" />
                <div className="flex-1 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Skeleton className="h-5 w-36" />
                      <Skeleton className="h-3 w-48" />
                    </div>
                    <Skeleton className="h-6 w-20 rounded-full" />
                  </div>
                  <div className="flex gap-2">
                    {[1, 2, 3].map(j => (
                      <Skeleton key={j} className="h-5 w-16 rounded-full" />
                    ))}
                  </div>
                  <Skeleton className="h-9 w-28 rounded-xl" />
                </div>
              </SkeletonCard>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// EARNINGS PAGE SKELETON
// ─────────────────────────────────────────────────────────────────────────────
export function EarningsSkeleton() {
  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Skeleton className="h-8 w-36" />
          <Skeleton className="h-4 w-56" />
        </div>
        <Skeleton className="h-10 w-44 rounded-xl" />
      </div>

      {/* Stats Cards */}
      <div className="grid md:grid-cols-3 gap-4">
        {[1, 2, 3].map(i => (
          <SkeletonCard key={i} className="p-4">
            <div className="flex items-center gap-3">
              <Skeleton className="h-10 w-10 rounded-xl" />
              <div className="space-y-1">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-6 w-28" />
              </div>
            </div>
          </SkeletonCard>
        ))}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <Skeleton className="h-10 w-32 rounded-xl" />
        <Skeleton className="h-10 w-24 rounded-xl" />
      </div>

      {/* Table */}
      <SkeletonCard className="p-0 overflow-hidden">
        {/* Table Header */}
        <div className="flex items-center gap-4 p-4 border-b border-border/50 bg-muted/30">
          <Skeleton className="h-4 w-32 flex-1" />
          <Skeleton className="h-4 w-28 flex-1" />
          <Skeleton className="h-4 w-24 flex-1" />
          <Skeleton className="h-4 w-20 flex-1" />
          <Skeleton className="h-4 w-28 flex-1" />
        </div>
        {/* Table Rows */}
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} className="flex items-center gap-4 p-4 border-b border-border/30 last:border-0">
            <div className="flex items-center gap-3 flex-1">
              <Skeleton className="h-8 w-8 rounded-full" />
              <Skeleton className="h-4 w-28" />
            </div>
            <Skeleton className="h-4 w-24 flex-1" />
            <Skeleton className="h-4 w-20 flex-1" />
            <Skeleton className="h-6 w-20 rounded-full flex-1" />
            <Skeleton className="h-4 w-24 flex-1" />
          </div>
        ))}
        {/* Pagination */}
        <div className="flex items-center justify-between p-4 border-t border-border/50">
          <Skeleton className="h-4 w-32" />
          <div className="flex gap-2">
            <Skeleton className="h-8 w-8 rounded-lg" />
            <Skeleton className="h-8 w-8 rounded-lg" />
            <Skeleton className="h-8 w-8 rounded-lg" />
          </div>
        </div>
      </SkeletonCard>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// ADDONS PAGE SKELETON
// ─────────────────────────────────────────────────────────────────────────────
export function AddOnsSkeleton() {
  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Skeleton className="h-8 w-36" />
          <Skeleton className="h-4 w-64" />
        </div>
        <Skeleton className="h-10 w-36 rounded-xl" />
      </div>

      {/* Search & Filters */}
      <div className="flex gap-3">
        <Skeleton className="h-10 flex-1 max-w-sm rounded-xl" />
        <Skeleton className="h-10 w-40 rounded-xl" />
      </div>

      {/* AddOns Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[1, 2, 3, 4, 5, 6].map(i => (
          <SkeletonCard key={i}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <Skeleton className="h-10 w-10 rounded-xl" />
                <div className="space-y-1">
                  <Skeleton className="h-5 w-32" />
                  <Skeleton className="h-3 w-24" />
                </div>
              </div>
              <Skeleton className="h-6 w-16 rounded-full" />
            </div>
            <Skeleton className="h-4 w-full" />
            <div className="flex items-center justify-between pt-2">
              <Skeleton className="h-6 w-20" />
              <div className="flex gap-2">
                <Skeleton className="h-8 w-8 rounded-lg" />
                <Skeleton className="h-8 w-8 rounded-lg" />
              </div>
            </div>
          </SkeletonCard>
        ))}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// PREFERENCES PAGE SKELETON
// ─────────────────────────────────────────────────────────────────────────────
export function PreferencesSkeleton() {
  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Skeleton className="h-8 w-44" />
          <Skeleton className="h-4 w-64" />
        </div>
        <Skeleton className="h-10 w-10 rounded-full" />
      </div>

      {/* Plan Card */}
      <SkeletonCard className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-xl" />
            <div className="space-y-1">
              <Skeleton className="h-5 w-24" />
              <Skeleton className="h-3 w-32" />
            </div>
          </div>
          <Skeleton className="h-9 w-28 rounded-xl" />
        </div>
      </SkeletonCard>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-border/50 pb-2">
        {[1, 2, 3, 4].map(i => (
          <Skeleton key={i} className="h-9 w-28 rounded-lg" />
        ))}
      </div>

      {/* Settings Form */}
      <div className="grid md:grid-cols-2 gap-6">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="space-y-2">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-10 w-full rounded-xl" />
            <Skeleton className="h-3 w-48" />
          </div>
        ))}
      </div>

      {/* Save Button */}
      <div className="flex justify-end pt-4 border-t border-border/50">
        <Skeleton className="h-10 w-32 rounded-xl" />
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// METRICS PAGE SKELETON
// ─────────────────────────────────────────────────────────────────────────────
export function MetricsSkeleton() {
  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-4 w-56" />
        </div>
        <Skeleton className="h-10 w-36 rounded-xl" />
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map(i => (
          <SkeletonCard key={i} className="p-4">
            <div className="space-y-2">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-7 w-24" />
              <Skeleton className="h-3 w-16" />
            </div>
          </SkeletonCard>
        ))}
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6">
        <SkeletonCard>
          <div className="flex items-center justify-between mb-4">
            <Skeleton className="h-5 w-36" />
            <Skeleton className="h-8 w-24 rounded-lg" />
          </div>
          <Skeleton className="h-64 w-full rounded-xl" />
        </SkeletonCard>
        <SkeletonCard>
          <div className="flex items-center justify-between mb-4">
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-8 w-24 rounded-lg" />
          </div>
          <Skeleton className="h-64 w-full rounded-xl" />
        </SkeletonCard>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// BILLING PAGE SKELETON
// ─────────────────────────────────────────────────────────────────────────────
export function BillingSkeleton() {
  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header */}
      <div className="space-y-2">
        <Skeleton className="h-8 w-36" />
        <Skeleton className="h-4 w-64" />
      </div>

      {/* Current Plan */}
      <SkeletonCard>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Skeleton className="h-14 w-14 rounded-2xl" />
            <div className="space-y-2">
              <Skeleton className="h-6 w-32" />
              <Skeleton className="h-4 w-48" />
            </div>
          </div>
          <div className="flex gap-3">
            <Skeleton className="h-10 w-32 rounded-xl" />
            <Skeleton className="h-10 w-28 rounded-xl" />
          </div>
        </div>
      </SkeletonCard>

      {/* Usage Stats */}
      <div className="grid md:grid-cols-3 gap-4">
        {[1, 2, 3].map(i => (
          <SkeletonCard key={i} className="p-4">
            <Skeleton className="h-4 w-24 mb-2" />
            <Skeleton className="h-2 w-full rounded-full mb-2" />
            <Skeleton className="h-3 w-20" />
          </SkeletonCard>
        ))}
      </div>

      {/* Payment Methods */}
      <SkeletonCard>
        <div className="flex items-center justify-between mb-4">
          <Skeleton className="h-5 w-44" />
          <Skeleton className="h-9 w-36 rounded-xl" />
        </div>
        <div className="space-y-3">
          {[1, 2].map(i => (
            <div key={i} className="flex items-center justify-between p-3 rounded-xl border border-border/50">
              <div className="flex items-center gap-3">
                <Skeleton className="h-10 w-14 rounded-lg" />
                <div className="space-y-1">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-3 w-24" />
                </div>
              </div>
              <Skeleton className="h-8 w-20 rounded-lg" />
            </div>
          ))}
        </div>
      </SkeletonCard>

      {/* Invoices */}
      <SkeletonCard>
        <Skeleton className="h-5 w-32 mb-4" />
        <div className="space-y-2">
          {[1, 2, 3].map(i => (
            <div key={i} className="flex items-center justify-between p-3 rounded-lg border border-border/30">
              <div className="flex items-center gap-4">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-5 w-16 rounded-full" />
              </div>
              <Skeleton className="h-8 w-24 rounded-lg" />
            </div>
          ))}
        </div>
      </SkeletonCard>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// PROPOSAL DETAILS SKELETON
// ─────────────────────────────────────────────────────────────────────────────
export function ProposalDetailsSkeleton() {
  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Back Button & Status */}
      <div className="flex items-center justify-between">
        <Skeleton className="h-9 w-24 rounded-xl" />
        <Skeleton className="h-7 w-28 rounded-full" />
      </div>

      {/* Client Info */}
      <SkeletonCard>
        <div className="flex items-start gap-4">
          <Skeleton className="h-16 w-16 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-7 w-48" />
            <div className="flex items-center gap-4">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-4 w-32" />
            </div>
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-10 w-10 rounded-xl" />
            <Skeleton className="h-10 w-10 rounded-xl" />
          </div>
        </div>
      </SkeletonCard>

      {/* Event Details */}
      <div className="grid md:grid-cols-2 gap-6">
        <SkeletonCard>
          <Skeleton className="h-5 w-32 mb-4" />
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="flex items-center gap-3">
                <Skeleton className="h-9 w-9 rounded-xl" />
                <div className="space-y-1">
                  <Skeleton className="h-3 w-16" />
                  <Skeleton className="h-4 w-32" />
                </div>
              </div>
            ))}
          </div>
        </SkeletonCard>
        <SkeletonCard>
          <Skeleton className="h-5 w-28 mb-4" />
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-28" />
            </div>
            <div className="flex items-center justify-between">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-4 w-24" />
            </div>
            <div className="border-t border-border/50 pt-3 flex items-center justify-between">
              <Skeleton className="h-5 w-16" />
              <Skeleton className="h-6 w-32" />
            </div>
          </div>
        </SkeletonCard>
      </div>

      {/* Actions */}
      <div className="flex gap-3 justify-end">
        <Skeleton className="h-10 w-28 rounded-xl" />
        <Skeleton className="h-10 w-36 rounded-xl" />
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// PUBLIC PAYMENT PAGE SKELETON
// ─────────────────────────────────────────────────────────────────────────────
export function PublicPaymentSkeleton() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted/30 p-4 sm:p-8">
      <div className="max-w-2xl mx-auto space-y-6 animate-in fade-in duration-300">
        {/* Logo */}
        <div className="flex justify-center">
          <Skeleton className="h-10 w-32" />
        </div>

        {/* Main Card */}
        <SkeletonCard className="overflow-hidden p-0">
          {/* Header */}
          <div className="p-6 border-b border-border/50 bg-muted/20">
            <div className="flex items-center gap-4">
              <Skeleton className="h-14 w-14 rounded-2xl" />
              <div className="space-y-2">
                <Skeleton className="h-6 w-48" />
                <Skeleton className="h-4 w-64" />
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Event Details */}
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="flex items-center gap-3">
                  <Skeleton className="h-5 w-5 rounded" />
                  <Skeleton className="h-4 w-40" />
                </div>
              ))}
            </div>

            {/* Package Info */}
            <div className="p-4 rounded-xl bg-muted/30 space-y-3">
              <div className="flex items-center justify-between">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-6 w-24" />
              </div>
              <Skeleton className="h-4 w-full" />
            </div>

            {/* Payment Summary */}
            <div className="space-y-2 pt-4 border-t border-border/50">
              <div className="flex items-center justify-between">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-24" />
              </div>
              <div className="flex items-center justify-between">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-4 w-20" />
              </div>
              <div className="flex items-center justify-between pt-2">
                <Skeleton className="h-5 w-12" />
                <Skeleton className="h-6 w-28" />
              </div>
            </div>

            {/* Pay Button */}
            <Skeleton className="h-12 w-full rounded-xl" />
          </div>
        </SkeletonCard>

        {/* Footer */}
        <div className="flex justify-center">
          <Skeleton className="h-3 w-48" />
        </div>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// GENERIC PAGE SKELETON (Fallback)
// ─────────────────────────────────────────────────────────────────────────────
export function PageSkeleton() {
  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-2">
          <Skeleton className="h-8 w-44" />
          <Skeleton className="h-4 w-64" />
        </div>
        <Skeleton className="h-10 w-32 rounded-xl" />
      </div>

      {/* Content Cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[1, 2, 3].map(i => (
          <SkeletonCard key={i}>
            <Skeleton className="h-5 w-3/4 mb-2" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
          </SkeletonCard>
        ))}
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// EXPORTS
// ─────────────────────────────────────────────────────────────────────────────
export {
  DashboardSkeleton as DashboardLoading,
  ProposalsSkeleton as ProposalsLoading,
  PackagesSkeleton as PackagesLoading,
  ContractTemplatesSkeleton as ContractTemplatesLoading,
  IntegrationsSkeleton as IntegrationsLoading,
  EarningsSkeleton as EarningsLoading,
  AddOnsSkeleton as AddOnsLoading,
  PreferencesSkeleton as PreferencesLoading,
  MetricsSkeleton as MetricsLoading,
  BillingSkeleton as BillingLoading,
  ProposalDetailsSkeleton as ProposalDetailsLoading,
  PublicPaymentSkeleton as PublicPaymentLoading,
  PageSkeleton as GenericLoading,
}
